import React, { useRef, useState } from 'react'
import { withStripeElements } from 'lib/stripe'
import { useSnackbar } from 'notistack'
import * as yup from 'yup'
import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { FormProvider, useForm } from 'react-hook-form'
import qs from 'query-string'
import { useLocation, useParams } from 'react-router-dom'
import Grid from '@material-ui/core/Grid'
import Divider from '@material-ui/core/Divider'
import InitialsAvatar from 'components/avatars/initials-avatar'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import { currency } from 'lib/utils/string'
import PaymentInformation from 'pages/schedule/ReviewBooking/payment-information'
import Button from '@material-ui/core/Button'
import { useStyles } from '../styles'
import ReservationsWaiver from './reservations-waiver'
import { yupResolver } from '@hookform/resolvers'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import StripePaymentElementsForm from './stripe-payment-elements-form'
import { useGetClientSecretAndTaxes } from '../queries'
import Error from 'components/error'
import RouterLinkButton from 'components/router-link-button'

const validationSchema = (showWaiver, isPaymentIntentsEnabled) =>
  yup.object().shape({
    ...(!showWaiver && {
      payment: yup.object().shape({
        name: isPaymentIntentsEnabled
          ? yup.string()
          : yup.string().required('This field is required'),
      }),
    }),
    ...(showWaiver && {
      agreeWaiver: yup
        .boolean()
        .oneOf([true], 'You must agree to the waiver to continue'),
    }),
  })

const ReviewReservationLayout = ({
  student,
  setIsComplete,
  setCardBrand,
  setLastDigits,
  setSpaceName,
  showWaiver,
  setShowWaiver,
  salesTaxEstimation,
  selectedPracticeSession,
}) => {
  const studentName = `${student.firstName} ${student.lastName}`

  const { enqueueSnackbar } = useSnackbar()
  const [isPaymentIntentsEnabled] = useFeatureFlags([
    flags.FLAG_PRACTICE_SESSIONS_PAYMENT_INTENTS,
  ])

  const form = useForm({
    resolver: yupResolver(
      validationSchema(showWaiver, isPaymentIntentsEnabled),
    ),
    defaultValues: {
      payment: {
        name: studentName,
      },
      agreeWaiver: false,
    },
  })
  const stripe = useStripe()
  const elements = useElements()
  const classes = useStyles()
  const [stripeToken, setStripeToken] = useState(null)
  const stripePaymentFormRef = useRef(null)

  const location = useLocation()
  const { code } = useParams()
  const {
    memberNumber,
    startDateTime,
    practiceSessionTypeId,
    studentId,
  } = qs.parse(location.search)

  const {
    clientSecret,
    taxesInCents: taxesInCentsFromPaymentIntent,
    loading,
    error: clientSecretError,
    errorMessage: clientSecretErrorMessage,
  } = useGetClientSecretAndTaxes({
    practiceSessionTypeId,
    startDateTime,
    studentId,
    studentMemberNumber: memberNumber,
    isPaymentIntentsEnabled,
  })

  const priceInCents = selectedPracticeSession.priceInCents
  const priceInDollars = priceInCents ? priceInCents / 100 : null
  const salesTaxInCentsFromTaxEstimation = priceInDollars
    ? salesTaxEstimation
    : 0
  const salesTaxInCents = isPaymentIntentsEnabled
    ? taxesInCentsFromPaymentIntent
    : salesTaxInCentsFromTaxEstimation
  const totalInCents = priceInCents + salesTaxInCents
  const totalInDollars = totalInCents / 100

  const onSubmit = async formValues => {
    const { error: stripeError, token } = await stripe.createToken(
      elements.getElement(CardNumberElement),
      {
        name: formValues.payment.name,
        address_country: 'US',
      },
    )
    if (stripeError) {
      enqueueSnackbar(stripeError.message, {
        variant: 'error',
      })
      return Promise.resolve()
    }
    setStripeToken(token)
    setShowWaiver(true)
  }

  const handleButtonClick = async () => {
    if (isPaymentIntentsEnabled) {
      if (stripePaymentFormRef.current?.validatePayment) {
        const isValid = await stripePaymentFormRef.current.validatePayment()
        if (!isValid) {
          return
        }
      }
      setShowWaiver(true)
    } else {
      await form.handleSubmit(onSubmit)()
    }
  }

  if (clientSecretError) return <Error error={clientSecretError} />

  return (
    <>
      <ReservationsWaiver
        showWaiver={showWaiver}
        form={form}
        stripeToken={stripeToken}
        setIsComplete={setIsComplete}
        setCardBrand={setCardBrand}
        setLastDigits={setLastDigits}
        setSpaceName={setSpaceName}
        selectedPracticeSession={selectedPracticeSession}
        stripePaymentFormRef={stripePaymentFormRef}
      />
      <Grid container style={{ display: showWaiver ? 'none' : 'block' }}>
        <Grid item md={7} xs={12} className={classes.gridItem}>
          <Grid container item xs={12} md={5} className={classes.studentCard}>
            <InitialsAvatar
              name={`${student.firstName} ${student.lastName}`}
              classes={classes}
            />
            <div className={classes.infoWrapped}>
              <Typography
                variant="caption"
                className={`${classes.fontBolded} ${classes.captionText}`}
                data-testid="student-name"
              >
                {studentName}
              </Typography>
              <Typography
                variant="caption"
                className={`${classes.captionText} ${classes.lighterText}`}
              >
                Student
              </Typography>
            </div>
          </Grid>
          <Divider style={{ margin: '2rem 0px 1rem' }} />
          {priceInDollars !== null && (
            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body1" className={classes.lighterText}>
                  Subtotal
                </Typography>
                <Typography variant="body1" className={classes.lighterText}>
                  {currency(priceInDollars)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body1" className={classes.lighterText}>
                  Sales Tax
                </Typography>
                <Typography variant="body1" className={classes.lighterText}>
                  {currency(salesTaxInCents / 100)}
                </Typography>
              </Box>
              <Box
                display="flex"
                className={classes.infoRow}
                justifyContent="space-between"
                mb={1}
              >
                <Typography variant="body1" className={`${classes.fontBolded}`}>
                  Due Today
                </Typography>
                <Typography variant="body1" className={`${classes.fontBolded}`}>
                  {currency(totalInDollars)}
                </Typography>
              </Box>
              <Divider style={{ margin: '1rem 0px 1rem' }} />
            </Box>
          )}
          <FormProvider {...form}>
            <form>
              <Box mb={4}>
                {isPaymentIntentsEnabled ? (
                  <StripePaymentElementsForm
                    ref={stripePaymentFormRef}
                    clientSecret={clientSecret}
                    secretLoading={loading}
                    stripe={stripe}
                    elements={elements}
                  />
                ) : (
                  <PaymentInformation
                    lessonType={{ acceptsOnlinePayments: true }}
                    paymentRequired={true}
                    setPaymentRequired={() => {}}
                  />
                )}
              </Box>
              {clientSecret === null && clientSecretErrorMessage ? (
                <Grid item>
                  <Typography color="error" variant="body1" gutterBottom>
                    {clientSecretErrorMessage}
                  </Typography>
                  <RouterLinkButton
                    variant="contained"
                    color="primary"
                    to={
                      code
                        ? `/pga-coaching-center-at-pga-frisco/reservations/${code}`
                        : '/pga-coaching-center-at-pga-frisco/reservations'
                    }
                    data-testid="retry-reservation-button"
                  >
                    Retry Reservation
                  </RouterLinkButton>
                </Grid>
              ) : (
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleButtonClick}
                    data-testid="continue-bay-reservation-button"
                    disabled={form.formState.isSubmitting}
                  >
                    Continue
                  </Button>
                </Grid>
              )}
            </form>
          </FormProvider>
        </Grid>
      </Grid>
    </>
  )
}

export default withStripeElements(ReviewReservationLayout)
